import React, { useState, useCallback, useMemo } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Divider from '@mui/material/Divider';
import ClickAwayListener from '@mui/material/ClickAwayListener';

import { KonnectAnalytics } from '../../../KonnectAnalytics/konnectAnalytics';
import { styles } from '../WorkingHours.styles';
import { TimeSlot, WorkingDay } from '../WorkingHours.types';
import { getSlotAlphaLabel, isFullDaySlot, minutesToTime } from '../utils/slotLabelUtils';
import { MINUTES_PER_DAY } from '../workingHoursConstants';

type DayRowMobileProps = {
  day: WorkingDay;
  selectedSite: string;
  onToggleDay: (dayId: string) => void;
  onAddSlot: (payload: { dayId: string; slotId: string }) => void;
  onUpdateSlot: (payload: {
    dayId: string;
    slotId: string;
    startMinutes: number;
    endMinutes: number;
  }) => void;
  onRemoveSlot: (payload: { dayId: string; slotId: string }) => void;
  onRestoreDay: (payload: { dayId: string; slots: TimeSlot[]; enabled: boolean }) => void;
  readOnly: boolean;
};

const timeInputToMinutes = (value: string): number => {
  const [hours, mins] = value.split(':').map(Number);
  return hours * 60 + mins;
};

// type="time" expects "HH:MM" in range 00:00–23:59, so we normalise with modulo
const minutesToTimeInput = (minutes: number): string =>
  minutesToTime(((minutes % MINUTES_PER_DAY) + MINUTES_PER_DAY) % MINUTES_PER_DAY);

const DayRowMobile = ({
  day,
  selectedSite,
  onToggleDay,
  onAddSlot,
  onUpdateSlot,
  onRemoveSlot,
  onRestoreDay,
  readOnly,
}: DayRowMobileProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const [snapshot, setSnapshot] = useState<{ slots: TimeSlot[]; enabled: boolean } | null>(null);

  const sortedSlots = useMemo(
    () => [...day.slots].sort((a, b) => a.startMinutes - b.startMinutes),
    [day.slots]
  );

  const enterEditMode = useCallback(() => {
    if (isEditing) return;
    setSnapshot({ slots: day.slots, enabled: day.enabled });
    setIsEditing(true);
  }, [isEditing, day.slots, day.enabled]);

  const cancelEdit = useCallback(
    (snap: { slots: TimeSlot[]; enabled: boolean } | null) => {
      if (snap) {
        onRestoreDay({ dayId: day.id, slots: snap.slots, enabled: snap.enabled });
      }
      setIsEditing(false);
      setSnapshot(null);
    },
    [day.id, onRestoreDay]
  );

  const handleNewTimeFrame = () => {
    enterEditMode();
    const slotId = `${day.id}-slot-${Date.now()}`;
    onAddSlot({ dayId: day.id, slotId });
    KonnectAnalytics.workingHoursAddSlot({ dayId: day.id, slotId, site: selectedSite });
  };

  const handleSave = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsEditing(false);
    setSnapshot(null);
    KonnectAnalytics.productionHoursDayRowAction({ action: 'save', dayId: day.id, site: selectedSite });
  };

  const handleCancel = (e: React.MouseEvent) => {
    e.stopPropagation();
    KonnectAnalytics.productionHoursDayRowAction({ action: 'cancel', dayId: day.id, site: selectedSite });
    cancelEdit(snapshot);
  };

  const handleStartChange = (slot: TimeSlot, value: string) => {
    if (!value) return;
    onUpdateSlot({
      dayId: day.id,
      slotId: slot.id,
      startMinutes: timeInputToMinutes(value),
      endMinutes: slot.endMinutes,
    });
  };

  const handleEndChange = (slot: TimeSlot, value: string) => {
    if (!value) return;
    onUpdateSlot({
      dayId: day.id,
      slotId: slot.id,
      startMinutes: slot.startMinutes,
      endMinutes: timeInputToMinutes(value),
    });
  };

  const handleToggleSwitch = () => {
    if (!day.enabled) {
      enterEditMode();
      onToggleDay(day.id);
      const slotId = `${day.id}-slot-${Date.now()}`;
      onAddSlot({ dayId: day.id, slotId });
      KonnectAnalytics.workingHoursAddSlot({ dayId: day.id, slotId, site: selectedSite });
    } else {
      enterEditMode();
      onToggleDay(day.id);
      KonnectAnalytics.workingHoursToggleDay({ dayId: day.id, enabled: false, site: selectedSite });
    }
  };

  // Ignore clicks that land inside ANY other day row - days are editable independently and
  // concurrently (see DayRow.tsx's handleClickAway for the same fix on desktop).
  const handleClickAway = (event: MouseEvent | TouchEvent) => {
    const target = event.target as HTMLElement;
    if (target.closest?.('[data-day-row]')) return;
    if (isEditing) cancelEdit(snapshot);
  };

  return (
    <ClickAwayListener onClickAway={handleClickAway}>
      <Box data-day-row={day.id} sx={isEditing ? styles.mobileDayRowEditing : styles.mobileDayRow}>
        <Box sx={styles.mobileDayHeader}>
          <Switch checked={day.enabled} onChange={handleToggleSwitch} size="small" disabled={readOnly} />
          <Typography sx={day.enabled ? styles.dayName : styles.dayNameDisabled}>
            {day.enabled ? day.name : `${day.name} (Disabled)`}
          </Typography>
          <Box sx={styles.mobileDayActions}>
            {isEditing ? (
              <>
                <Button
                  variant="contained"
                  size="small"
                  onClick={handleSave}
                  sx={styles.saveButton}>
                  Save
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleCancel}
                  sx={styles.cancelButton}>
                  Cancel
                </Button>
              </>
            ) : (
              <Button
                variant="contained"
                size="small"
                disabled={readOnly || !day.enabled}
                onClick={handleNewTimeFrame}
                sx={styles.newTimeFrameButton}>
                + New Time frame
              </Button>
            )}
          </Box>
        </Box>

        {day.enabled && sortedSlots.length > 0 && (
          <Box sx={styles.mobileSlotList}>
            {sortedSlots.map((slot, index) => (
              <Box key={slot.id}>
                {index > 0 && <Divider sx={styles.mobileSlotDivider} />}
                <Box sx={styles.mobileSlotRow}>
                  <Typography sx={styles.mobileSlotLabel}>
                    {isFullDaySlot(slot) ? 'Full Day' : getSlotAlphaLabel(index)}
                  </Typography>
                  {!isFullDaySlot(slot) && (
                    <>
                      <TextField
                        type="time"
                        size="small"
                        value={minutesToTimeInput(slot.startMinutes)}
                        disabled={readOnly}
                        onChange={e => {
                          enterEditMode();
                          handleStartChange(slot, e.target.value);
                        }}
                        sx={styles.mobileTimeInput}
                        inputProps={{ step: 900 }}
                      />
                      <Typography sx={styles.mobileSlotSeparator}>–</Typography>
                      <TextField
                        type="time"
                        size="small"
                        value={minutesToTimeInput(slot.endMinutes)}
                        disabled={readOnly}
                        onChange={e => {
                          enterEditMode();
                          handleEndChange(slot, e.target.value);
                        }}
                        sx={styles.mobileTimeInput}
                        inputProps={{ step: 900 }}
                      />
                    </>
                  )}
                  <IconButton
                    size="small"
                    disabled={readOnly}
                    onClick={() => {
                      enterEditMode();
                      onRemoveSlot({ dayId: day.id, slotId: slot.id });
                      KonnectAnalytics.workingHoursRemoveSlot({
                        dayId: day.id,
                        slotId: slot.id,
                        site: selectedSite,
                        source: 'bar',
                      });
                    }}
                    sx={styles.mobileSlotDeleteButton}>
                    ×
                  </IconButton>
                </Box>
              </Box>
            ))}
          </Box>
        )}
      </Box>
    </ClickAwayListener>
  );
};

export default DayRowMobile;
